import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  RoastBatch,
  Blend,
  VisionEvent,
  OvenConfig,
  SensorReading,
  startBatch,
  startTimer,
  finishBatch,
  cancelBatch,
  deleteBatch,
  createBatch,
  listBlends,
  updateBatch,
  getOvenConfig,
  setOvenConfig,
  getLatestVisionEvent,
  listVisionDevices,
  getSensorReadings,
  getModbusDevices,
} from '../api/client';
import { useAppData } from '../context/AppDataContext';
import { localDateString } from '../utils/date';
import TemperatureChart from '../components/operator/TemperatureChart';
import ColorHistogram from '../components/operator/ColorHistogram';
import RoastScaleDisplay from '../components/operator/RoastScaleDisplay';
import ColorLabelModal from '../components/operator/ColorLabelModal';
import { playTimerDoneSound, playColorTargetSound, resumeAudioContext } from '../utils/alerts';
import { ovenQueuePosition } from '../utils/ovenQueue';

type OperatorPhase = 'select' | 'waiting-beans' | 'roasting';

function todayStr(): string {
  return localDateString();
}

function formatCountdown(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

function isUnknownBlend(batch: RoastBatch): boolean {
  return batch.blend_id === 'unknown' || batch.blend_name === 'Unknown';
}

// Phase register address (beans loaded: ≥1, dump: =7)
const PHASE_ADDR = '400017';
const WEIGHT_ADDR = '400019';

// ── Blend Selector Banner ─────────────────────────────────────────────

function BlendSelectorBanner({
  batch,
  onBlendSet,
}: {
  batch: RoastBatch;
  onBlendSet: (updated: RoastBatch) => void;
}) {
  const [blends, setBlends] = useState<Blend[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    listBlends().then(setBlends).catch(() => {});
  }, []);

  const handleSave = async () => {
    if (!selectedId) return;
    const blend = blends.find((b) => b.id === selectedId);
    if (!blend) return;
    setSaving(true);
    try {
      const updated = await updateBatch(batch.id, {
        blend_id: blend.id,
        blend_name: blend.name,
      });
      onBlendSet(updated);
    } catch (err) {
      console.error('Failed to update blend:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-amber-100 border-2 border-amber-400 rounded-2xl px-4 py-3 flex items-center gap-3 shrink-0">
      <span className="text-amber-800 font-bold text-sm whitespace-nowrap">⚠️ בחר תערובת:</span>
      <select
        value={selectedId}
        onChange={(e) => setSelectedId(e.target.value)}
        className="flex-1 px-3 py-1.5 border-2 border-amber-300 rounded-xl text-sm focus:ring-2 focus:ring-amber-500 outline-none bg-white"
      >
        <option value="">-- בחר --</option>
        {blends.map((b) => (
          <option key={b.id} value={b.id}>{b.name}</option>
        ))}
      </select>
      <button
        onClick={handleSave}
        disabled={!selectedId || saving}
        className="px-4 py-1.5 bg-amber-700 text-white rounded-xl text-sm font-bold hover:bg-amber-800 disabled:opacity-50 touch-manipulation"
      >
        {saving ? '...' : 'שמור'}
      </button>
    </div>
  );
}

// ── Completed Sidebar with color-label blinking ──────────────────────

function CompletedSidebar({
  batches,
  onBatchUpdated,
}: {
  batches: RoastBatch[];
  onBatchUpdated: (b: RoastBatch) => void;
}) {
  const [labelingBatch, setLabelingBatch] = useState<RoastBatch | null>(null);
  const [blink, setBlink] = useState(false);
  const now = Date.now();

  // Blink ticker for the labeling button
  useEffect(() => {
    const id = setInterval(() => setBlink((v) => !v), 700);
    return () => clearInterval(id);
  }, []);

  const sorted = [...batches].sort((a, b) => {
    if (!a.end_time || !b.end_time) return 0;
    return new Date(b.end_time).getTime() - new Date(a.end_time).getTime();
  });

  return (
    <>
      <div className="w-72 shrink-0 bg-green-50 border-r-2 border-green-200 rounded-2xl p-4 flex flex-col overflow-hidden">
        <h2 className="text-xl font-bold text-green-800 mb-4 shrink-0">
          ✅ הושלמו ({batches.length})
        </h2>
        {batches.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-4">אין batchs שהושלמו</p>
        ) : (
          <div className="space-y-3 overflow-y-auto flex-1 min-h-0">
            {sorted.map((batch) => {
              const deadline = batch.color_label_deadline ? new Date(batch.color_label_deadline).getTime() : null;
              const withinWindow = deadline != null && now < deadline && !batch.actual_roast_label;
              const remainingSec = deadline ? Math.max(0, Math.floor((deadline - now) / 1000)) : 0;
              const remainingMin = Math.floor(remainingSec / 60);
              const remainingSecPart = remainingSec % 60;

              return (
                <div
                  key={batch.id}
                  className={`rounded-xl p-3 shadow-sm border ${
                    isUnknownBlend(batch)
                      ? 'bg-orange-50 border-orange-300'
                      : 'bg-white border-green-200'
                  }`}
                >
                  <div className="font-bold text-base text-gray-800 flex items-center gap-1">
                    {batch.blend_name}
                    {isUnknownBlend(batch) && (
                      <span className="text-xs font-normal text-orange-700 bg-orange-100 rounded px-1">⚠ לא ידוע</span>
                    )}
                  </div>
                  <div className="text-sm text-gray-500">
                    {batch.quantity} {batch.unit === 'kg' ? 'ק״ג' : 'batchs'}
                    {batch.batch_weight_kg != null && <span className="mr-2 font-bold text-amber-700">· {batch.batch_weight_kg} ק״ג לאצווה</span>}
                  </div>
                  {batch.end_time && (
                    <div className="text-sm text-green-700 font-semibold mt-1">
                      🕐 {new Date(batch.end_time).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  )}
                  {batch.start_time && batch.end_time && (
                    <div className="text-xs text-gray-400 mt-0.5">
                      משך: {Math.round((new Date(batch.end_time).getTime() - new Date(batch.start_time).getTime()) / 60000)} דק׳
                    </div>
                  )}

                  {/* Blend selector for auto-started unknown batches */}
                  {isUnknownBlend(batch) && (
                    <div className="mt-2">
                      <BlendSelectorBanner batch={batch} onBlendSet={onBatchUpdated} />
                    </div>
                  )}

                  {/* Color label UI */}
                  {!isUnknownBlend(batch) && batch.actual_roast_label ? (
                    <div className="mt-2 text-xs font-semibold text-amber-700 bg-amber-50 rounded-lg px-2 py-1">
                      🎨 {batch.actual_roast_label}
                    </div>
                  ) : !isUnknownBlend(batch) && withinWindow ? (
                    <button
                      onClick={() => setLabelingBatch(batch)}
                      className={`mt-2 w-full py-1.5 rounded-lg text-sm font-bold text-white transition-all ${
                        blink ? 'bg-blue-600' : 'bg-blue-400'
                      }`}
                    >
                      🏷 תייג צבע ({remainingMin}:{remainingSecPart.toString().padStart(2, '0')})
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {labelingBatch && (
        <ColorLabelModal
          batch={labelingBatch}
          onClose={() => setLabelingBatch(null)}
          onSaved={(updated) => {
            onBatchUpdated(updated);
            setLabelingBatch(null);
          }}
        />
      )}
    </>
  );
}

// ── Vision Config Modal ───────────────────────────────────────────────

function VisionConfigModal({
  machineId,
  currentDeviceId,
  onClose,
  onSave,
}: {
  machineId: string;
  currentDeviceId: string | null;
  onClose: () => void;
  onSave: (deviceId: string | null) => void;
}) {
  const [devices, setDevices] = useState<string[]>([]);
  const [selected, setSelected] = useState<string>(currentDeviceId || '');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listVisionDevices()
      .then((d) => setDevices(d))
      .catch(() => setDevices([]))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    const deviceId = selected || null;
    try {
      await setOvenConfig(machineId, deviceId, undefined);
      onSave(deviceId);
    } catch (err) {
      console.error('Failed to save oven config:', err);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm mx-4" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-xl font-bold text-gray-800 mb-4 text-center">מצלמת ויז׳ן — תנור {machineId}</h3>
        {loading ? (
          <p className="text-center text-gray-400 py-4">טוען...</p>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">בחר מכשיר</label>
              <select
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-xl text-base focus:ring-2 focus:ring-amber-500 outline-none"
              >
                <option value="">ללא מכשיר</option>
                {devices.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button onClick={onClose} className="py-2 text-base font-bold text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200">
                ביטול
              </button>
              <button onClick={handleSave} className="py-2 text-base font-bold text-white bg-amber-700 rounded-xl hover:bg-amber-800">
                שמור
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Modbus Config Modal ───────────────────────────────────────────────

function ModbusConfigModal({
  machineId,
  currentDeviceId,
  onClose,
  onSave,
}: {
  machineId: string;
  currentDeviceId: string | null;
  onClose: () => void;
  onSave: (deviceId: string | null) => void;
}) {
  const [devices, setDevices] = useState<{ device_id: string; last_seen: string }[]>([]);
  const [selected, setSelected] = useState<string>(currentDeviceId || '');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getModbusDevices()
      .then((d) => {
        // Sort by last_seen DESC (most recent first)
        const sorted = [...d].sort((a, b) => b.last_seen.localeCompare(a.last_seen));
        setDevices(sorted);
      })
      .catch(() => setDevices([]))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    const deviceId = selected || null;
    try {
      await setOvenConfig(machineId, undefined, deviceId);
      onSave(deviceId);
    } catch (err) {
      console.error('Failed to save modbus config:', err);
    }
  };

  function formatLastSeen(iso: string): string {
    const diffMs = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'עכשיו';
    if (mins < 60) return `לפני ${mins} דק׳`;
    const hrs = Math.floor(mins / 60);
    return `לפני ${hrs} שעות`;
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm mx-4" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-xl font-bold text-gray-800 mb-4 text-center">מכשיר Modbus — תנור {machineId}</h3>
        {loading ? (
          <p className="text-center text-gray-400 py-4">טוען...</p>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">בחר מכשיר Modbus</label>
              {devices.length === 0 ? (
                <p className="text-sm text-gray-400 py-2">לא נמצאו מכשירים פעילים (2 שעות אחרונות)</p>
              ) : (
                <select
                  value={selected}
                  onChange={(e) => setSelected(e.target.value)}
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-xl text-base focus:ring-2 focus:ring-blue-500 outline-none"
                >
                  <option value="">ללא מכשיר</option>
                  {devices.map((d) => (
                    <option key={d.device_id} value={d.device_id}>
                      {d.device_id} ({formatLastSeen(d.last_seen)})
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button onClick={onClose} className="py-2 text-base font-bold text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200">
                ביטול
              </button>
              <button onClick={handleSave} className="py-2 text-base font-bold text-white bg-blue-700 rounded-xl hover:bg-blue-800">
                שמור
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Waiting for Beans ─────────────────────────────────────────────────

function WaitingForBeans({
  batch,
  onManualStart,
  onCancel,
}: {
  batch: RoastBatch;
  onManualStart: () => void;
  onCancel: () => void;
}) {
  const [dot, setDot] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setDot((d) => (d + 1) % 4), 500);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="flex flex-col items-center justify-center h-full gap-8 bg-amber-50 rounded-3xl border-4 border-amber-200 p-12 text-center">
      <div className="text-6xl animate-pulse">☕</div>
      <div>
        <h2 className="text-3xl font-extrabold text-amber-800 mb-2">{batch.blend_name}</h2>
        <p className="text-xl text-amber-600 font-semibold">
          ממתין לטעינת פולים{'.'.repeat(dot + 1)}
        </p>
        <p className="text-sm text-gray-400 mt-2">זיהוי אוטומטי ממודבוס</p>
      </div>
      <div className="flex gap-4">
        <button
          onClick={onManualStart}
          className="px-10 py-5 bg-amber-700 text-white rounded-2xl text-2xl font-extrabold hover:bg-amber-800 active:bg-amber-900 transition-colors shadow-xl touch-manipulation"
        >
          🔥 התחל ידנית
        </button>
        <button
          onClick={onCancel}
          className="px-8 py-5 bg-red-100 text-red-700 rounded-2xl text-xl font-extrabold hover:bg-red-200 border-2 border-red-300 touch-manipulation"
        >
          ✕ ביטול
        </button>
      </div>
    </div>
  );
}

// ── Active Roasting Dashboard ─────────────────────────────────────────

function RoastingDashboard({
  batch,
  roastTimeSeconds,
  visionEvent,
  sensorReadings,
  onFinish,
  onDump,
  onCancel,
  onTimerStart,
  onBlendSet,
}: {
  batch: RoastBatch;
  roastTimeSeconds: number;
  visionEvent: VisionEvent | null;
  sensorReadings: SensorReading[];
  onFinish: () => void;
  onDump: () => void;
  onCancel: () => void;
  onTimerStart: () => Promise<RoastBatch | null>;
  onBlendSet: (updated: RoastBatch) => void;
}) {
  const timerRunning = batch.timer_running ?? false;
  const serverRemaining = batch.remaining_seconds ?? roastTimeSeconds;
  const [remaining, setRemaining] = useState(timerRunning ? serverRemaining : roastTimeSeconds);
  const [isRunning, setIsRunning] = useState(timerRunning);
  const [timerAlarm, setTimerAlarm] = useState(timerRunning && serverRemaining <= 0);
  const [colorAlarm, setColorAlarm] = useState(false);
  const [flashClass, setFlashClass] = useState('');
  const intervalRef = useRef<number | null>(null);
  const timerAlertFiredRef = useRef(false);
  const colorAlertFiredRef = useRef(false);

  // Sync from server — timerAlarm latches true and is never reset by a stale server poll.
  useEffect(() => {
    if (batch.timer_running) {
      const rem = batch.remaining_seconds ?? 0;
      setRemaining(rem);
      setIsRunning(rem > 0);
      setTimerAlarm(prev => prev || rem <= 0);
    } else if (batch.timer_started_at) {
      setRemaining(0);
      setIsRunning(false);
      setTimerAlarm(true);
    }
  }, [batch.remaining_seconds, batch.timer_running, batch.timer_started_at]);

  // Local countdown
  useEffect(() => {
    if (!isRunning) return;
    intervalRef.current = window.setInterval(() => {
      setRemaining((prev) => {
        if (prev <= 1) {
          if (intervalRef.current) clearInterval(intervalRef.current);
          setIsRunning(false);
          setTimerAlarm(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [isRunning]);

  // Timer-done alert: one-shot flash + sound on first trigger.
  useEffect(() => {
    if (timerAlarm && !timerAlertFiredRef.current) {
      timerAlertFiredRef.current = true;
      playTimerDoneSound().catch(() => {});
      setFlashClass('flash-black');
      setTimeout(() => setFlashClass(''), 2000);
    }
  }, [timerAlarm]);

  // Repeat alarm sound every 12 s while timer is done and batch not yet finished.
  useEffect(() => {
    if (!timerAlarm) return;
    const id = setInterval(() => playTimerDoneSound().catch(() => {}), 12000);
    return () => clearInterval(id);
  }, [timerAlarm]);

  // Color-target alert (red flash + sound)
  useEffect(() => {
    if (
      visionEvent?.stop_recommended &&
      visionEvent.target_distance != null &&
      visionEvent.target_distance <= 18 &&
      !colorAlertFiredRef.current
    ) {
      colorAlertFiredRef.current = true;
      setColorAlarm(true);
      playColorTargetSound().catch(() => {});
      setFlashClass('flash-red');
      setTimeout(() => setFlashClass(''), 2000);
    }
  }, [visionEvent?.stop_recommended, visionEvent?.target_distance]);

  const handleStartOven = async () => {
    const updated = await onTimerStart();
    if (updated) {
      setRemaining(updated.remaining_seconds ?? roastTimeSeconds);
      setIsRunning(true);
      timerAlertFiredRef.current = false;
    }
  };

  const progress = roastTimeSeconds > 0 ? ((roastTimeSeconds - remaining) / roastTimeSeconds) * 100 : 0;

  return (
    <>
      {/* Flash overlay */}
      {flashClass && (
        <div
          className={`fixed inset-0 z-50 pointer-events-none ${
            flashClass === 'flash-black' ? 'bg-black' : 'bg-red-600'
          } animate-ping opacity-60`}
        />
      )}

      <div className="flex flex-col gap-3 h-full">
        {/* Blend selector banner for auto-started unknown batches */}
        {isUnknownBlend(batch) && (
          <BlendSelectorBanner batch={batch} onBlendSet={onBlendSet} />
        )}

        {/* Top: Timer centered */}
        <div className="flex justify-center shrink-0">
          <div className={`w-full max-w-lg rounded-2xl px-6 py-3 flex flex-col items-center border-4 ${
            timerAlarm ? 'bg-gray-900 border-gray-600 animate-pulse' : colorAlarm ? 'bg-red-50 border-red-400' : 'bg-amber-50 border-amber-300'
          }`}>
            <div className="flex items-center gap-4 w-full justify-center mb-1">
              <span className="text-sm font-bold text-gray-400">תנור {batch.machine_id}</span>
              <span className="text-base font-bold text-gray-600 truncate">{batch.blend_name}</span>
            </div>
            <div className={`text-7xl font-mono font-extrabold leading-none mb-2 ${
              timerAlarm ? 'text-white' : remaining <= 30 && isRunning ? 'text-orange-600' : 'text-amber-800'
            }`}>
              {formatCountdown(remaining)}
            </div>
            {isRunning && (
              <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                <div
                  className={`h-3 rounded-full transition-all duration-1000 ${timerAlarm ? 'bg-gray-400' : 'bg-amber-600'}`}
                  style={{ width: `${progress}%` }}
                />
              </div>
            )}
            {timerAlarm && (
              <div className="text-white font-bold text-lg mb-2 animate-bounce">🔔 סיום!</div>
            )}
            {colorAlarm && !timerAlarm && (
              <div className="text-red-600 font-bold text-sm mb-2 animate-bounce">🎨 הגיע לצבע יעד!</div>
            )}

            {/* Controls */}
            <div className="flex gap-2 w-full justify-center">
              {!isRunning && !timerAlarm && (
                <button
                  onClick={handleStartOven}
                  className="px-8 py-2 bg-amber-700 text-white rounded-xl text-lg font-extrabold hover:bg-amber-800 touch-manipulation"
                >
                  🔥 התחל
                </button>
              )}
              {(isRunning || timerAlarm) && (
                <button
                  onClick={onDump}
                  className="px-8 py-2 bg-red-600 text-white rounded-xl text-base font-extrabold hover:bg-red-700 touch-manipulation"
                >
                  🚪 סיום קלייה
                </button>
              )}
              {timerAlarm && (
                <button
                  onClick={onFinish}
                  className="px-8 py-2 bg-green-600 text-white rounded-xl text-base font-extrabold hover:bg-green-700 touch-manipulation"
                >
                  ✓ סיים
                </button>
              )}
              {!timerAlarm && (
                <button
                  onClick={onCancel}
                  className="px-6 py-2 bg-gray-100 text-gray-600 rounded-xl text-sm font-bold hover:bg-gray-200 border border-gray-300 touch-manipulation"
                >
                  ✕ ביטול
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Temperature chart — full width */}
        <div className="flex-1 bg-white rounded-2xl border border-gray-200 p-3 min-h-0">
          <div className="text-xs font-semibold text-gray-500 mb-2">🌡 טמפרטורה</div>
          <div className="h-full" style={{ height: 'calc(100% - 24px)' }}>
            <TemperatureChart readings={sensorReadings} />
          </div>
        </div>

        {/* Bottom: Color histogram + Roast scale + Camera image */}
        <div className="flex gap-3 h-48 shrink-0">
          {/* Color histogram */}
          <div className="flex-1 bg-white rounded-2xl border border-gray-200 p-3 min-h-0">
            <div className="text-xs font-semibold text-gray-500 mb-1">🎨 היסטוגרמת קלייה</div>
            <div style={{ height: 'calc(100% - 20px)' }}>
              <ColorHistogram
                visionEvent={visionEvent}
                targetRoastScore={batch.blend_target_roast_score}
              />
            </div>
          </div>

          {/* Roast score scale */}
          <div className="flex-1 bg-white rounded-2xl border border-gray-200 p-3 min-h-0">
            <div className="text-xs font-semibold text-gray-500 mb-1">📊 סקאלת קלייה</div>
            <div style={{ height: 'calc(100% - 20px)' }}>
              <RoastScaleDisplay
                visionEvent={visionEvent}
                targetRoastScore={batch.blend_target_roast_score}
              />
            </div>
          </div>

          {/* Live camera image */}
          {(visionEvent?.full_image_url ?? visionEvent?.roi_image_url) && (
            <div className="w-44 shrink-0 bg-white rounded-2xl border border-gray-200 p-3 min-h-0 flex flex-col">
              <div className="text-xs font-semibold text-gray-500 mb-1">📷 מצלמה</div>
              <div className="relative flex-1 min-h-0">
                <img
                  src={visionEvent!.full_image_url ?? visionEvent!.roi_image_url!}
                  alt="תמונה אחרונה"
                  className="h-full w-full object-cover rounded-lg"
                />
                {visionEvent?.captured_at && (
                  <div className="absolute bottom-0 inset-x-0 rounded-b-lg bg-black/60 px-1 py-0.5 text-center">
                    <div className="text-[9px] font-bold text-white leading-tight tabular-nums">
                      {new Date(visionEvent.captured_at).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                    </div>
                    <div className="text-[8px] text-white/70 leading-tight">
                      {new Date(visionEvent.captured_at).toLocaleDateString('he-IL')}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ── Main Operator Screen ─────────────────────────────────────────────

function Operator() {
  const { machineId = '1' } = useParams<{ machineId: string }>();
  const planDate = todayStr();
  const { todayBatches, strayOccupants, ovenQueues, blends: contextBlends, allSensorReadings, refreshPlan: refreshSharedPlan } = useAppData();
  const [actionError, setActionError] = useState<string | null>(null);
  const [phase, setPhase] = useState<OperatorPhase>('select');
  const [planned, setPlanned] = useState<RoastBatch[]>([]);
  const [activeBatch, setActiveBatch] = useState<RoastBatch | null>(null);
  const [done, setDone] = useState<RoastBatch[]>([]);

  const blendTimes = useMemo(
    () => Object.fromEntries(contextBlends.map(b => [b.id, b.required_roast_time_seconds])),
    [contextBlends],
  );
  const [visionDeviceId, setVisionDeviceId] = useState<string | null>(null);
  const [modbusDeviceId, setModbusDeviceId] = useState<string | null>(null);
  const [latestVisionEvent, setLatestVisionEvent] = useState<VisionEvent | null>(null);
  const latestReadings = useMemo(
    () => phase === 'select' || !modbusDeviceId
      ? []
      : allSensorReadings.filter(reading => reading.device_id === modbusDeviceId),
    [allSensorReadings, modbusDeviceId, phase],
  );
  // chartReadings: 1-hour time series from Athena (used for TemperatureChart)
  const [chartReadings, setChartReadings] = useState<SensorReading[]>([]);
  const [showVisionConfig, setShowVisionConfig] = useState(false);
  const [showModbusConfig, setShowModbusConfig] = useState(false);

  // Baseline sensor values for beans-loaded detection
  const baselineWeightRef = useRef<number | null>(null);

  // Sync local planned/active/done state whenever the shared context updates.
  useEffect(() => {
    const p: RoastBatch[] = [];
    let ip: RoastBatch | null = null;
    const d: RoastBatch[] = [];

    for (const b of [...todayBatches, ...strayOccupants]) {
      if (b.status === 'planned' && (!b.machine_id || b.machine_id === machineId)) p.push(b);
      else if (b.status === 'in-progress' && b.machine_id === machineId) ip = b;
      else if (b.status === 'done') d.push(b);
    }
    setPlanned(p.sort((a, b) =>
      (ovenQueuePosition(ovenQueues, a) || 3) - (ovenQueuePosition(ovenQueues, b) || 3)
      || a.sort_order - b.sort_order));
    if (ip) {
      setActiveBatch(ip);
      const modbusStartedRoast = isUnknownBlend(ip) || Boolean(ip.timer_started_at);
      setPhase(prev =>
        modbusStartedRoast ? 'roasting' : prev === 'select' ? 'waiting-beans' : prev,
      );
    } else {
      setActiveBatch(null);
      setPhase('select');
      baselineWeightRef.current = null;
    }
    setDone(d);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todayBatches, strayOccupants, ovenQueues, machineId]);

  // Oven vision + modbus device assignment
  useEffect(() => {
    getOvenConfig(machineId)
      .then((config: OvenConfig) => {
        setVisionDeviceId(config.vision_device_id ?? null);
        setModbusDeviceId(config.modbus_device_id ?? null);
      })
      .catch(() => { setVisionDeviceId(null); setModbusDeviceId(null); });
  }, [machineId]);

  // Vision event polling (5s when roasting)
  useEffect(() => {
    if (!visionDeviceId || phase !== 'roasting') { setLatestVisionEvent(null); return; }
    const fetch = () => getLatestVisionEvent(visionDeviceId).then(setLatestVisionEvent).catch(() => {});
    fetch();
    const id = setInterval(fetch, 5000);
    return () => clearInterval(id);
  }, [visionDeviceId, phase]);

  // Historical time series (Athena, 10 s) — used for TemperatureChart only.
  useEffect(() => {
    if (phase === 'select') { setChartReadings([]); return; }
    const fetch = async () => {
      if (document.hidden) return;
      try {
        const readings = await getSensorReadings(1, modbusDeviceId ?? undefined);
        setChartReadings(readings);
      } catch { /* ignore */ }
    };
    fetch();
    const id = setInterval(fetch, 10000);
    return () => clearInterval(id);
  }, [phase, modbusDeviceId]);

  // Beans-loaded auto-detection
  useEffect(() => {
    if (phase !== 'waiting-beans' || latestReadings.length === 0) return;

    const phaseReg = latestReadings.find((r) => r.register_address === PHASE_ADDR);
    const weightReg = latestReadings.find((r) => r.register_address === WEIGHT_ADDR);

    const phaseVal = phaseReg ? parseFloat(phaseReg.value) : null;
    const weightVal = weightReg ? parseFloat(weightReg.value) : null;

    // Set baseline on first reading
    if (baselineWeightRef.current === null && weightVal !== null) {
      baselineWeightRef.current = weightVal;
      return;
    }

    const beansLoadedByPhase = phaseVal !== null && phaseVal >= 1;
    const beansLoadedByWeight = weightVal !== null && baselineWeightRef.current !== null && weightVal > baselineWeightRef.current + 2000;

    if (beansLoadedByPhase || beansLoadedByWeight) {
      handleBeansDetected();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestReadings, phase]);

  // Dump auto-detection (phase=7 or weight drop)
  useEffect(() => {
    if (phase !== 'roasting' || latestReadings.length === 0 || !activeBatch?.timer_started_at) return;

    const phaseReg = latestReadings.find((r) => r.register_address === PHASE_ADDR);
    const weightReg = latestReadings.find((r) => r.register_address === WEIGHT_ADDR);

    const phaseVal = phaseReg ? parseFloat(phaseReg.value) : null;
    const weightVal = weightReg ? parseFloat(weightReg.value) : null;

    const dumpByPhase = phaseVal !== null && phaseVal === 7;
    const peakWeight = baselineWeightRef.current;
    const dumpByWeight = weightVal !== null && peakWeight !== null && weightVal < peakWeight - 3000;

    if (dumpByPhase || dumpByWeight) {
      handleDump();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestReadings, phase]);

  const handleSelectBatch = async (batchId: string) => {
    if (activeBatch) return;
    const waitingIds = ovenQueues[machineId]?.pending_batch_ids ?? [];
    if (waitingIds.length && waitingIds[0] !== batchId) return;
    resumeAudioContext();
    setActionError(null);
    try {
      const updated = await startBatch(batchId, machineId);
      setPlanned((prev) => prev.filter((b) => b.id !== batchId));
      setActiveBatch(updated);
      baselineWeightRef.current = null;
      setPhase('waiting-beans');
      refreshSharedPlan();
    } catch (err) {
      console.error('Failed to start batch:', err);
      setActionError('לא ניתן להתחיל את האצווה. ייתכן שתור התנור השתנה; התכנית מתרעננת.');
      await refreshSharedPlan();
    }
  };

  const handleUsePlannedBatchForActive = useCallback(async (plannedBatch: RoastBatch) => {
    if (!activeBatch || plannedBatch.machine_id) return;
    try {
      // If the active batch already has a real blend, put it back into the plan
      if (!isUnknownBlend(activeBatch)) {
        const returned = await createBatch(
          activeBatch.blend_id,
          activeBatch.quantity,
          activeBatch.unit,
          planDate,
          activeBatch.batch_weight_kg ?? 90,
          activeBatch.planned_roast_time_seconds
            ?? activeBatch.timer_duration_seconds
            ?? blendTimes[activeBatch.blend_id]
            ?? 600,
        );
        setPlanned((prev) =>
          [...prev, ...(Array.isArray(returned) ? returned : [returned])].sort(
            (a, b) => a.sort_order - b.sort_order,
          ),
        );
      }
      const updated = await updateBatch(activeBatch.id, {
        blend_id: plannedBatch.blend_id,
        blend_name: plannedBatch.blend_name,
        quantity: plannedBatch.quantity,
        unit: plannedBatch.unit,
        planned_roast_time_seconds: plannedBatch.planned_roast_time_seconds
          ?? blendTimes[plannedBatch.blend_id]
          ?? 600,
      });
      await deleteBatch(plannedBatch.id);
      setActiveBatch(updated);
      setPlanned((prev) => prev.filter((b) => b.id !== plannedBatch.id));
      refreshSharedPlan();
    } catch (err) {
      console.error('Failed to set active roast blend from plan:', err);
    }
  }, [activeBatch, blendTimes, planDate, refreshSharedPlan]);

  const handleBeansDetected = useCallback(async () => {
    if (!activeBatch) return;
    try {
      const updated = await startTimer(activeBatch.id);
      setActiveBatch(updated);
      setPhase('roasting');
    } catch (err) {
      console.error('Failed to start timer:', err);
    }
  }, [activeBatch]);

  const handleManualStart = useCallback(async () => {
    if (!activeBatch) return;
    try {
      const updated = await startTimer(activeBatch.id);
      setActiveBatch(updated);
      setPhase('roasting');
    } catch (err) {
      console.error('Failed to start timer:', err);
    }
  }, [activeBatch]);

  const handleStartTimer = useCallback(async (): Promise<RoastBatch | null> => {
    if (!activeBatch) return null;
    try {
      const updated = await startTimer(activeBatch.id);
      setActiveBatch(updated);
      return updated;
    } catch (err) {
      console.error('Failed to start timer:', err);
      return null;
    }
  }, [activeBatch]);

  const handleDump = useCallback(async () => {
    if (!activeBatch) return;
    try {
      const updated = await finishBatch(activeBatch.id);
      setDone((prev) => [updated, ...prev]);
      setActiveBatch(null);
      setPhase('select');
      refreshSharedPlan();
    } catch (err) {
      console.error('Failed to finish batch:', err);
    }
  }, [activeBatch, refreshSharedPlan]);

  const handleFinishBatch = useCallback(async () => {
    if (!activeBatch) return;
    try {
      const updated = await finishBatch(activeBatch.id);
      setDone((prev) => [updated, ...prev]);
      setActiveBatch(null);
      setPhase('select');
      refreshSharedPlan();
    } catch (err) {
      console.error('Failed to finish batch:', err);
    }
  }, [activeBatch, refreshSharedPlan]);

  const handleCancelBatch = useCallback(async () => {
    if (!activeBatch) return;
    try {
      const updated = await cancelBatch(activeBatch.id);
      setPlanned((prev) => [...prev, updated].sort((a, b) => a.sort_order - b.sort_order));
      setActiveBatch(null);
      setPhase('select');
      refreshSharedPlan();
    } catch (err) {
      console.error('Failed to cancel batch:', err);
    }
  }, [activeBatch, refreshSharedPlan]);

  const handleBatchUpdated = useCallback((updated: RoastBatch) => {
    setDone((prev) => prev.map((b) => (b.id === updated.id ? updated : b)));
    refreshSharedPlan();
  }, [refreshSharedPlan]);

  // When blend is set on the active (unknown) batch, update activeBatch state
  const handleActiveBlendSet = useCallback((updated: RoastBatch) => {
    setActiveBatch(updated);
  }, []);

  const roastTime = activeBatch
    ? (
      activeBatch.timer_duration_seconds
      ?? activeBatch.planned_roast_time_seconds
      ?? blendTimes[activeBatch.blend_id]
      ?? 600
    )
    : 0;

  return (
    <div className="flex flex-col flex-1 min-h-0 gap-4">
      {actionError && <div role="alert" className="rounded-xl bg-red-50 p-3 font-bold text-red-800">{actionError}</div>}
      {/* ── Header ── */}
      <div className="flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <img src="/icon.png" alt="דאדא" className="h-9 w-auto rounded-xl border border-amber-300/50 shadow-sm" />
          <h1 className="text-3xl font-extrabold text-gray-800">תנור {machineId} — ביצוע קלייה</h1>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-lg text-gray-500 font-medium">{planDate}</span>
          <button
            onClick={() => setShowModbusConfig(true)}
            title="הגדרת מכשיר Modbus"
            className={`p-2 rounded-xl border-2 text-lg transition-colors ${
              modbusDeviceId
                ? 'border-blue-400 bg-blue-50 text-blue-700 hover:bg-blue-100'
                : 'border-gray-300 bg-gray-50 text-gray-500 hover:bg-gray-100'
            }`}
          >
            📡 {modbusDeviceId ? modbusDeviceId : 'ללא Modbus'}
          </button>
          <button
            onClick={() => setShowVisionConfig(true)}
            title="הגדרת מצלמת ויז׳ן"
            className={`p-2 rounded-xl border-2 text-lg transition-colors ${
              visionDeviceId
                ? 'border-amber-400 bg-amber-50 text-amber-700 hover:bg-amber-100'
                : 'border-gray-300 bg-gray-50 text-gray-500 hover:bg-gray-100'
            }`}
          >
            📷 {visionDeviceId ? visionDeviceId : 'ללא מצלמה'}
          </button>
        </div>
      </div>

      {/* ── Three-Column Layout ── */}
      <div className="flex gap-4 items-stretch flex-1 min-h-0" dir="ltr">
        {/* Completed sidebar */}
        <CompletedSidebar batches={done} onBatchUpdated={handleBatchUpdated} />

        {/* Center: active phase */}
        <div className="flex-1 min-w-0 overflow-y-auto">
          {phase === 'select' && (
            <div className="bg-gray-50 rounded-3xl border-4 border-dashed border-gray-300 p-12 text-center h-full flex flex-col items-center justify-center">
              <span className="text-8xl mb-6 opacity-30">☕</span>
              <p className="text-3xl text-gray-400 font-bold mb-2">אין קלייה פעילה</p>
              <p className="text-xl text-gray-400">בחר אצווה מהתכנית בצד</p>
            </div>
          )}

          {phase === 'waiting-beans' && activeBatch && (
            <div className="flex flex-col gap-4 h-full">
              {activeBatch.loading_worker_name && (
                <div dir="rtl" className="rounded-xl border-2 border-teal-200 bg-teal-50 px-4 py-3 text-lg font-bold text-teal-800">
                  👤 העמסת קפה ירוק: {activeBatch.loading_worker_name}
                </div>
              )}
              <WaitingForBeans
                batch={activeBatch}
                onManualStart={handleManualStart}
                onCancel={handleCancelBatch}
              />
              <div className="bg-white rounded-2xl border border-gray-200 p-3 flex-1 min-h-0">
                <div className="text-xs font-semibold text-gray-500 mb-2">🌡 טמפרטורה</div>
                <div style={{ height: 'calc(100% - 24px)' }}>
                  <TemperatureChart readings={chartReadings} />
                </div>
              </div>
            </div>
          )}

          {phase === 'roasting' && activeBatch && (
            <div className="flex h-full flex-col gap-2">
              {activeBatch.loading_worker_name && (
                <div dir="rtl" className="rounded-lg bg-teal-50 px-3 py-2 text-sm font-bold text-teal-800">
                  👤 העמסה: {activeBatch.loading_worker_name}
                </div>
              )}
              <div className="min-h-0 flex-1">
                <RoastingDashboard
                  key={activeBatch.id}
                  batch={activeBatch}
                  roastTimeSeconds={roastTime}
                  visionEvent={latestVisionEvent}
                  sensorReadings={chartReadings}
                  onFinish={handleFinishBatch}
                  onDump={handleDump}
                  onCancel={handleCancelBatch}
                  onTimerStart={handleStartTimer}
                  onBlendSet={handleActiveBlendSet}
                />
              </div>
            </div>
          )}
        </div>

        {/* Planned queue */}
        <div className="w-72 shrink-0 bg-blue-50 border-2 border-blue-200 rounded-2xl p-4 flex flex-col overflow-hidden" dir="rtl">
          <h2 className="text-xl font-bold text-blue-800 mb-4 shrink-0">
            📋 תכנית ({planned.length})
          </h2>
          {planned.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">אין batchs מתוכננות</p>
          ) : (
            <div className="space-y-3 overflow-y-auto flex-1 min-h-0">
              {planned.map((batch, idx) => (
                <button
                  key={batch.id}
                  onClick={() => activeBatch ? handleUsePlannedBatchForActive(batch) : handleSelectBatch(batch.id)}
                  disabled={activeBatch ? !!batch.machine_id
                    : (ovenQueues[machineId]?.pending_batch_ids.length ?? 0) > 0
                      && ovenQueues[machineId]?.pending_batch_ids[0] !== batch.id}
                  className={`w-full bg-white border-2 rounded-xl p-3 text-center transition-all touch-manipulation shadow-sm ${
                    activeBatch
                      ? 'border-orange-300 hover:border-orange-500 hover:shadow-md active:bg-orange-50 cursor-pointer'
                      : idx === 0
                        ? 'border-amber-400 ring-2 ring-amber-200 hover:shadow-md active:bg-amber-50 cursor-pointer'
                        : 'border-blue-300 hover:border-amber-400 hover:shadow-md active:bg-amber-50 cursor-pointer'
                  } disabled:cursor-not-allowed disabled:opacity-50`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                      idx === 0 ? 'bg-amber-500 text-white' : 'bg-blue-200 text-blue-700'
                    }`}>
                      {idx + 1}
                    </span>
                    <span className="font-bold text-base text-gray-800">{batch.blend_name}</span>
                  </div>
                  <div className="text-sm text-gray-500">
                    {batch.quantity} {batch.unit === 'kg' ? 'ק״ג' : 'אצווה'}
                    {batch.batch_weight_kg != null && <span className="mr-2 font-bold text-amber-700">· {batch.batch_weight_kg} ק״ג לאצווה</span>}
                  </div>
                  {batch.blend_target_roast_score != null && (
                    <div className="text-xs text-amber-600 mt-0.5">יעד: {Math.round(batch.blend_target_roast_score)}</div>
                  )}
                  {idx === 0 && !activeBatch && (
                    <div className="text-xs text-amber-600 font-semibold mt-1">הבא בתור ◀</div>
                  )}
                  {batch.machine_id && (
                    <div className="text-xs text-blue-700 font-semibold mt-1">הועמסה — מקום {ovenQueuePosition(ovenQueues, batch)} בתור לתנור {machineId}</div>
                  )}
                  {activeBatch && !batch.machine_id && (
                    <div className="text-xs text-orange-700 font-semibold mt-1">השתמש לתערובת הקלייה הפעילה</div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {showVisionConfig && (
        <VisionConfigModal
          machineId={machineId}
          currentDeviceId={visionDeviceId}
          onClose={() => setShowVisionConfig(false)}
          onSave={(deviceId) => {
            setVisionDeviceId(deviceId);
            setShowVisionConfig(false);
          }}
        />
      )}

      {showModbusConfig && (
        <ModbusConfigModal
          machineId={machineId}
          currentDeviceId={modbusDeviceId}
          onClose={() => setShowModbusConfig(false)}
          onSave={(deviceId) => {
            setModbusDeviceId(deviceId);
            setShowModbusConfig(false);
          }}
        />
      )}
    </div>
  );
}

export default Operator;
